import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import {
  IPatientHistory,
  IPatientRequestDetail,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';

@Component({
  selector: 'afoone-view-request',
  templateUrl: './view-request.component.html',
  styleUrls: ['./view-request.component.css'],
})
export class ViewRequestComponent implements OnInit {
  record: IPatientRequestDetail | null = null;
  patient: IPatientHistory | null = null;
  loading = true;
  error: string | null = null;
  /** Pantalla 1.4 desde tabla de acumulados: no mostrar panel Acumulados [[19]]. */
  hideAcumuladosPanel = false;
  /** Pantalla 1.4 desde epidemiología: no mostrar panel Epidemiología / sensibilidad ATB (#671). */
  hideEpidemiologyPanel = false;
  private centroId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: PatientRequestsService,
    private keycloak: KeycloakService,
    private userService: UsersService,
    private msgbox: MsgboxService
  ) {}

  /** Espera a que haya token (pestaña nueva puede tardar en tener sesión). */
  private async waitForToken(maxWaitMs = 5000): Promise<boolean> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const token = await this.keycloak.getToken();
      if (token) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(async (qp) => {
      const laboratory = (qp['laboratorio'] ?? qp['laboratory'] ?? '').toString().trim();
      const idRequest = (qp['peticion'] ?? qp['idRequest'] ?? '').toString().trim();
      const labNr = Number(qp['labor'] ?? qp['laboratoryNumber'] ?? 0);
      const date = (qp['date'] ?? qp['einDat'] ?? '').toString().trim();
      const impreso = (qp['impreso'] ?? '.').toString().trim() || '.';
      const sitio = (qp['sitio'] ?? 'D').toString().trim() || 'D';
      this.hideAcumuladosPanel = qp['fromAcumulados'] === '1' || qp['fromAcumulados'] === true;
      this.hideEpidemiologyPanel = qp['fromEpidemiology'] === '1' || qp['fromEpidemiology'] === true;
      const centroIdParam = qp['centroId'];
      if (centroIdParam != null && centroIdParam !== '') {
        const c = Number(centroIdParam);
        if (!Number.isNaN(c)) {
          this.centroId = c;
          this.userService.setSisifoDefaultCenter(c);
        }
      } else {
        this.centroId = null;
      }
      if (!laboratory || !idRequest || !date) {
        this.loading = false;
        this.error = 'Faltan parámetros (laboratorio, petición, fecha).';
        return;
      }
      const hasToken = await this.waitForToken();
      if (!hasToken) {
        this.loading = false;
        this.error = 'Sesión no disponible. Cierra esta pestaña e intenta de nuevo desde la pestaña original.';
        return;
      }
      this.service
        .getPatientRequestDetail(
          laboratory.slice(0, 3).toUpperCase(),
          idRequest,
          labNr,
          date,
          impreso === '-' ? '.' : impreso,
          sitio,
          this.currentCenterId()
        )
        .subscribe({
          next: (detail) => {
            this.record = detail;
            this.loading = false;
            this.loadPatientForDetail(detail);
          },
          error: (err) => {
            this.loading = false;
            this.error = err?.message || err?.error?.message || 'Error al cargar la petición.';
          },
        });
    });
  }

  private loadPatientForDetail(detail: IPatientRequestDetail): void {
    const historiaNorm = detail?.aZ?.trim();
    if (!historiaNorm) return;

    this.service
      .getPatientHistories({
        history: historiaNorm,
        searchType: 'C',
        centroId: this.currentCenterId(),
      } as any)
      .subscribe({
        next: (list) => {
          this.patient = list?.find((p) => (p.historia?.trim() ?? '') === historiaNorm) ?? null;
        },
        error: (err) => this.showPatientLookupError(err),
      });
  }

  private showPatientLookupError(err: any): void {
    const key = err?.error?.message || err?.message;
    if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
      this.msgbox.showError(key);
    } else {
      this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
    }
  }

  private currentCenterId(): number | null {
    return this.centroId ?? this.userService.getSisifoDefaultCenter();
  }
}
